import { useState, useRef } from "react";
import { Upload, X, Image as ImgIcon, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

export default function MediaUploader({ value = [], onChange, testIdPrefix = "media" }) {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const uploadFiles = async (files) => {
    setUploading(true);
    const next = [...value];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push({ url: data.url, kind: data.kind, original_filename: data.original_filename });
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
    }
    onChange(next);
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) uploadFiles(files);
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    e.target.value = "";
  };

  const remove = (i) => {
    const next = value.filter((_, idx) => idx !== i);
    onChange(next);
  };

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div data-testid={`${testIdPrefix}-uploader`}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed transition px-6 py-10 text-center ${drag ? "border-[#d4af37] bg-[#d4af37]/5" : "border-white/15 hover:border-[#d4af37]/60"}`}
        data-testid={`${testIdPrefix}-dropzone`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={onPick}
          className="hidden"
          data-testid={`${testIdPrefix}-input`}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-3 text-[#d4af37]">
            <Loader2 className="h-5 w-5 animate-spin" /> Yükleniyor...
          </div>
        ) : (
          <>
            <Upload className="h-8 w-8 text-[#d4af37] mx-auto" />
            <div className="mt-3 font-display text-lg">Sürükle & Bırak veya tıkla</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">Resim ve / veya Video · Birden fazla</div>
            <div className="mt-2 font-mono text-[10px] text-white/35">Hiç medya yüklemezsen otomatik &quot;İsim/Logo&quot; referansı olarak kaydedilir</div>
          </>
        )}
      </div>

      {value.length > 0 && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`${testIdPrefix}-preview`}>
          {value.map((m, i) => (
            <div key={`${m.url}-${i}`} className="relative group aspect-square overflow-hidden border border-white/10 bg-[#0a0a0a]">
              {m.kind === "video" ? (
                <video src={mediaSrc(m.url)} muted className="w-full h-full object-cover" />
              ) : (
                <img src={mediaSrc(m.url)} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute top-1 left-1 text-[10px] font-mono uppercase tracking-[0.2em] bg-[#0a0a0a]/80 border border-white/15 text-[#d4af37] px-1.5 py-0.5 inline-flex items-center gap-1">
                {m.kind === "video" ? <Film className="h-3 w-3" /> : <ImgIcon className="h-3 w-3" />} {i + 1}
              </div>
              <div className="absolute inset-0 bg-[#0a0a0a]/70 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                <button type="button" onClick={() => move(i, -1)} className="h-8 px-2 text-[10px] border border-white/20 hover:border-[#d4af37] text-white font-mono uppercase tracking-[0.2em]">←</button>
                <button type="button" onClick={() => move(i, 1)} className="h-8 px-2 text-[10px] border border-white/20 hover:border-[#d4af37] text-white font-mono uppercase tracking-[0.2em]">→</button>
                <button type="button" onClick={() => remove(i)} data-testid={`${testIdPrefix}-remove-${i}`} className="h-8 w-8 bg-[#d4af37] text-[#121212] inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
